/**
 * AdsPage — Google Ads Dashboard
 *
 * Campaign performance, search terms analysis, geo data, and change history.
 * Admin-only — shows what NEOSEM does with the ads budget.
 */

import { useEffect, useState } from 'react';
import { useAdsStore } from '../stores/ads-store';
import type { CampaignMetrics, SearchTermRow, GeoRow, ChangeRow, OptimizationRow } from '../stores/ads-store';

// ─── Helpers ────────────────────────────────────────────────────────────────

function fmt(n: number | null | undefined, decimals = 0): string {
  if (n == null) return '-';
  return new Intl.NumberFormat('nl-NL', { minimumFractionDigits: decimals, maximumFractionDigits: decimals }).format(n);
}

function eur(n: number | null | undefined): string {
  if (n == null) return '-';
  return `\u20AC${fmt(n, 2)}`;
}

function pct(n: number | null | undefined): string {
  if (n == null) return '-';
  return `${fmt(n * 100, 1)}%`;
}

function delta(n: number | null | undefined): string {
  if (n == null) return '';
  const sign = n > 0 ? '+' : '';
  return `${sign}${fmt(n, 1)}%`;
}

function deltaColor(n: number | null | undefined, invert = false): string {
  if (n == null) return 'text-gray-400';
  const positive = invert ? n < 0 : n > 0;
  return positive ? 'text-emerald-600' : n === 0 ? 'text-gray-400' : 'text-red-600';
}

// ─── Summary Cards ──────────────────────────────────────────────────────────

function SummaryBar() {
  const { totals, dateRange } = useAdsStore();
  if (!totals) return null;

  const cpaDelta = totals.cpa && totals.prev_cpa
    ? ((totals.cpa - totals.prev_cpa) / totals.prev_cpa * 100) : null;

  const cards = [
    { label: 'Spend', value: eur(totals.cost_eur) },
    { label: 'Conversies', value: fmt(totals.conversions) },
    { label: 'CPA', value: eur(totals.cpa), delta: cpaDelta, invert: true },
    { label: 'Clicks', value: fmt(totals.clicks) },
  ];

  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
      {cards.map((card) => (
        <div key={card.label} className="bg-white rounded-xl border border-gray-200 p-4">
          <div className="text-sm text-gray-500">{card.label}</div>
          <div className="text-2xl font-semibold text-gray-900 mt-1">{card.value}</div>
          {'delta' in card && card.delta != null && (
            <div className={`text-sm mt-1 ${deltaColor(card.delta, card.invert)}`}>
              {delta(card.delta)} vs vorige {dateRange}d
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

// ─── Campaign Table ─────────────────────────────────────────────────────────

function CampaignTable() {
  const { campaigns } = useAdsStore();
  if (!campaigns.length) return <p className="text-gray-500 text-sm">Geen campagne data beschikbaar.</p>;

  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-gray-100 bg-gray-50">
              <th className="text-left px-4 py-3 font-medium text-gray-600">Campagne</th>
              <th className="text-right px-4 py-3 font-medium text-gray-600">Spend</th>
              <th className="text-right px-4 py-3 font-medium text-gray-600">Clicks</th>
              <th className="text-right px-4 py-3 font-medium text-gray-600">Conv.</th>
              <th className="text-right px-4 py-3 font-medium text-gray-600">CPA</th>
              <th className="text-right px-4 py-3 font-medium text-gray-600">IS</th>
              <th className="text-right px-4 py-3 font-medium text-gray-600">Budget Lost</th>
              <th className="text-right px-4 py-3 font-medium text-gray-600">Trend</th>
            </tr>
          </thead>
          <tbody>
            {campaigns.map((c: CampaignMetrics) => {
              const cpaColor = c.cpa == null ? '' : c.cpa < 15 ? 'text-emerald-700' : c.cpa > 30 ? 'text-red-700' : 'text-amber-700';
              return (
                <tr key={c.campaign_id} className="border-b border-gray-50 hover:bg-gray-50">
                  <td className="px-4 py-3">
                    <div className="font-medium text-gray-900 truncate max-w-[240px]">{c.campaign_name}</div>
                    <div className="text-xs text-gray-400">{c.campaign_status}</div>
                  </td>
                  <td className="text-right px-4 py-3">{eur(c.cost_eur)}</td>
                  <td className="text-right px-4 py-3">{fmt(c.clicks)}</td>
                  <td className="text-right px-4 py-3">{fmt(c.conversions)}</td>
                  <td className={`text-right px-4 py-3 font-medium ${cpaColor}`}>{eur(c.cpa)}</td>
                  <td className="text-right px-4 py-3">{pct(c.avg_impression_share)}</td>
                  <td className="text-right px-4 py-3 text-amber-600">{pct(c.avg_budget_lost_is)}</td>
                  <td className={`text-right px-4 py-3 ${deltaColor(c.conv_delta)}`}>
                    {delta(c.conv_delta)}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─── Search Terms ───────────────────────────────────────────────────────────

function SearchTermsPanel() {
  const { searchTerms, searchTermSummary } = useAdsStore();
  const [showWaste, setShowWaste] = useState(true);
  const [search, setSearch] = useState('');

  const filtered = searchTerms
    .filter((t: SearchTermRow) => showWaste ? t.is_waste : !t.is_waste)
    .filter((t: SearchTermRow) => !search || t.search_term.toLowerCase().includes(search.toLowerCase()));

  return (
    <div>
      {searchTermSummary && (
        <div className="flex gap-4 mb-4 text-sm">
          <span className="text-gray-600">
            <span className="font-medium text-red-600">{searchTermSummary.waste_terms}</span> verspillende termen
          </span>
          <span className="text-gray-600">
            Verspilde kosten: <span className="font-medium text-red-600">{eur(searchTermSummary.waste_cost_eur)}</span>
          </span>
        </div>
      )}

      <div className="flex gap-3 mb-4">
        <button
          onClick={() => setShowWaste(true)}
          className={`px-3 py-1.5 rounded-lg text-sm font-medium ${showWaste ? 'bg-red-100 text-red-800' : 'bg-gray-100 text-gray-600'}`}
        >
          Verspilling
        </button>
        <button
          onClick={() => setShowWaste(false)}
          className={`px-3 py-1.5 rounded-lg text-sm font-medium ${!showWaste ? 'bg-emerald-100 text-emerald-800' : 'bg-gray-100 text-gray-600'}`}
        >
          Top performers
        </button>
        <input
          type="text"
          placeholder="Zoek zoekterm..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="flex-1 px-3 py-1.5 rounded-lg border border-gray-200 text-sm"
        />
      </div>

      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto max-h-[500px] overflow-y-auto">
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-gray-50">
              <tr className="border-b border-gray-100">
                <th className="text-left px-4 py-3 font-medium text-gray-600">Zoekterm</th>
                <th className="text-left px-4 py-3 font-medium text-gray-600">Matched KW</th>
                <th className="text-right px-4 py-3 font-medium text-gray-600">Clicks</th>
                <th className="text-right px-4 py-3 font-medium text-gray-600">Kosten</th>
                <th className="text-right px-4 py-3 font-medium text-gray-600">Conv.</th>
                <th className="text-right px-4 py-3 font-medium text-gray-600">CPA</th>
              </tr>
            </thead>
            <tbody>
              {filtered.slice(0, 100).map((t: SearchTermRow, i: number) => (
                <tr key={`${t.search_term}-${i}`} className="border-b border-gray-50 hover:bg-gray-50">
                  <td className="px-4 py-2.5 max-w-[300px] truncate">{t.search_term}</td>
                  <td className="px-4 py-2.5 text-gray-500 max-w-[200px] truncate">{t.matched_keyword}</td>
                  <td className="text-right px-4 py-2.5">{fmt(t.clicks)}</td>
                  <td className="text-right px-4 py-2.5">{eur(t.cost_eur)}</td>
                  <td className="text-right px-4 py-2.5">{fmt(t.conversions)}</td>
                  <td className="text-right px-4 py-2.5">{eur(t.cpa)}</td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr><td colSpan={6} className="px-4 py-8 text-center text-gray-400">Geen zoektermen gevonden</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// ─── Geo Table ──────────────────────────────────────────────────────────────

function GeoTable() {
  const { geoData } = useAdsStore();
  if (!geoData.length) return <p className="text-gray-500 text-sm">Geen geografische data beschikbaar.</p>;

  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-gray-100 bg-gray-50">
              <th className="text-left px-4 py-3 font-medium text-gray-600">Provincie</th>
              <th className="text-right px-4 py-3 font-medium text-gray-600">Spend</th>
              <th className="text-right px-4 py-3 font-medium text-gray-600">Clicks</th>
              <th className="text-right px-4 py-3 font-medium text-gray-600">Conv.</th>
              <th className="text-right px-4 py-3 font-medium text-gray-600">CPA</th>
              <th className="text-right px-4 py-3 font-medium text-gray-600">CTR</th>
              <th className="text-right px-4 py-3 font-medium text-gray-600">Waarde</th>
            </tr>
          </thead>
          <tbody>
            {geoData.map((g: GeoRow) => {
              const cpaColor = g.cpa == null ? '' : g.cpa < 15 ? 'text-emerald-700' : g.cpa > 30 ? 'text-red-700' : 'text-amber-700';
              return (
                <tr key={g.province} className="border-b border-gray-50 hover:bg-gray-50">
                  <td className="px-4 py-3 font-medium text-gray-900">{g.province}</td>
                  <td className="text-right px-4 py-3">{eur(g.cost_eur)}</td>
                  <td className="text-right px-4 py-3">{fmt(g.clicks)}</td>
                  <td className="text-right px-4 py-3">{fmt(g.conversions)}</td>
                  <td className={`text-right px-4 py-3 font-medium ${cpaColor}`}>{eur(g.cpa)}</td>
                  <td className="text-right px-4 py-3">{g.ctr}%</td>
                  <td className="text-right px-4 py-3">{eur(g.conversion_value_eur)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─── Change Log ─────────────────────────────────────────────────────────────

function ChangeLog() {
  const { changes } = useAdsStore();
  if (!changes.length) return <p className="text-gray-500 text-sm">Geen recente wijzigingen.</p>;

  return (
    <div className="space-y-3">
      {changes.slice(0, 50).map((c: ChangeRow, i: number) => (
        <div key={`${c.change_datetime}-${i}`} className="bg-white rounded-xl border border-gray-200 p-4">
          <div className="flex items-center justify-between mb-1">
            <span className="font-medium text-gray-900 text-sm">{c.operation}</span>
            <span className="text-xs text-gray-400">
              {new Date(c.change_datetime).toLocaleString('nl-NL', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
            </span>
          </div>
          <div className="text-sm text-gray-600">
            <span className="text-blue-600">{c.user_email || 'System'}</span>
            {' '}wijzigde{' '}
            <span className="font-medium">{c.resource_type}</span>
            {c.campaign_name && <> in <span className="font-medium">{c.campaign_name}</span></>}
          </div>
        </div>
      ))}
    </div>
  );
}

// ─── Optimalisaties (eigen wijzigingen + resultaat) ─────────────────────────

const ACTOR_LABELS: Record<string, { label: string; cls: string }> = {
  claude: { label: 'Claude', cls: 'bg-indigo-100 text-indigo-800' },
  willem: { label: 'Willem', cls: 'bg-blue-100 text-blue-800' },
  neosem: { label: 'NEOSEM', cls: 'bg-gray-200 text-gray-700' },
  google: { label: 'Google', cls: 'bg-amber-100 text-amber-800' },
};

const VERDICT_LABELS: Record<string, { label: string; cls: string }> = {
  open: { label: 'Loopt', cls: 'bg-amber-100 text-amber-800' },
  success: { label: 'Succes', cls: 'bg-emerald-100 text-emerald-800' },
  neutral: { label: 'Neutraal', cls: 'bg-gray-100 text-gray-600' },
  failed: { label: 'Mislukt', cls: 'bg-red-100 text-red-700' },
  reverted: { label: 'Teruggedraaid', cls: 'bg-red-100 text-red-700' },
  closed: { label: 'Geëvalueerd', cls: 'bg-gray-100 text-gray-600' },
  proposed: { label: 'Voorstel', cls: 'bg-purple-100 text-purple-800' },
  vetoed: { label: 'Veto', cls: 'bg-red-100 text-red-700' },
  executed: { label: 'Uitgevoerd', cls: 'bg-emerald-100 text-emerald-800' },
};

/** Resterende vetotijd in uren, of null als het venster dicht is. */
function vetoUrenOver(opt: OptimizationRow): number | null {
  if (!opt.execute_after) return null;
  const ms = new Date(opt.execute_after).getTime() - Date.now();
  return ms > 0 ? ms / 3_600_000 : null;
}

// Norm herzien 01-09: trede 2 is sinds de degradatie van auto-negatives alleen
// nog auto-revert, dus het bewijs is (a) het oordeel (verdicts van de autonome
// run) en (b) de eerste schaduw-revert — wanneer die zich ook voordoet. Bewust
// GEEN deadline: schaduw kost niets, en gates horen zelden te raken. De oude
// actie-norm (5) was met de degradatie onhaalbaar geworden en zou de klok
// permanent rood houden om de verkeerde reden.
const GATE_NORM = { verdicts: 3, reverts: 1 };

/** Gate-klok: hoeveel bewijs ligt er, en is de flip naar live verantwoord?
 *  Bewust bewijs-gebaseerd i.p.v. kalender-gebaseerd — een klok op "2 weken
 *  verstreken" slaagt ook met nul waarnemingen, en dan flip je op niets.
 *  De flip geldt alleen voor auto-revert: dat is het enige dat nog trede 2 is. */
function GateKlok() {
  const { optimizations } = useAdsStore();

  // Schaduw-acties zijn sinds 01-09 per definitie reverts (auto-negatives zijn
  // gedegradeerd naar voorstellen); backtest-rijen tellen niet meer mee.
  const reverts = optimizations.filter((o) => o.source === 'shadow');
  const revertsOk = reverts.filter((o) => o.beoordeling === 'akkoord').length;
  const revertsNok = reverts.filter((o) => o.beoordeling === 'afgekeurd').length;
  const revertsOpen = reverts.filter((o) => !o.beoordeling).length;

  // Alleen verdicts die de AUTONOME run schreef tellen als bewijs. De 24 historische
  // verdicts (actor 'claude', in sessie met Willem) zeggen niets over de autonomie —
  // die meenemen zou de klok laten slagen zonder dat er iets getoetst is.
  const verdicts = optimizations.filter(
    (o) => o.verdict && o.source !== 'backtest' && o.actor === 'claude-cron'
  );
  const verdictsOk = verdicts.filter((o) => o.beoordeling === 'akkoord').length;
  const verdictsNok = verdicts.filter((o) => o.beoordeling === 'afgekeurd').length;
  const verdictsOpen = verdicts.filter((o) => !o.beoordeling).length;

  const klaar =
    verdictsOk >= GATE_NORM.verdicts &&
    revertsOk >= GATE_NORM.reverts &&
    revertsNok === 0 &&
    verdictsNok === 0;

  const Balk = ({ label, ok, nok, open, norm, wachttekst }: {
    label: string; ok: number; nok: number; open: number; norm: number; wachttekst?: string;
  }) => (
    <div className="flex items-center justify-between text-sm py-1">
      <span className="text-gray-700">{label}</span>
      <span className="tabular-nums">
        {ok + nok + open === 0 && wachttekst ? (
          <span className="text-gray-400">{wachttekst}</span>
        ) : (
          <>
            <span className={ok >= norm ? 'text-emerald-700 font-medium' : 'text-gray-900'}>{ok}</span>
            <span className="text-gray-400">/{norm} akkoord</span>
            {nok > 0 && <span className="text-red-700 ml-2">{nok} afgekeurd</span>}
            {open > 0 && <span className="text-amber-700 ml-2">{open} te beoordelen</span>}
          </>
        )}
      </span>
    </div>
  );

  return (
    <div className={`mb-4 rounded-xl border p-4 ${klaar ? 'border-emerald-200 bg-emerald-50' : 'border-gray-200 bg-gray-50'}`}>
      <div className="text-sm font-semibold text-gray-900 mb-1">
        Gate-klok trede 2 — mag auto-revert live?
      </div>
      <p className="text-xs text-gray-600 mb-2">
        Beoordeel per verdict of de conclusie klopt, en per schaduw-revert of je hem gewild had.
        Eén afkeuring houdt de flip tegen — dat is het punt. Er is geen deadline: schaduw kost
        niets, dus de flip wacht op bewijs, niet op de kalender.
      </p>
      <Balk label="Verdicts van de autonome run" ok={verdictsOk} nok={verdictsNok} open={verdictsOpen} norm={GATE_NORM.verdicts} />
      <Balk
        label="Schaduw-reverts (bij een geraakte gate)"
        ok={revertsOk} nok={revertsNok} open={revertsOpen} norm={GATE_NORM.reverts}
        wachttekst="nog geen gate geraakt — dat is de gezonde stand"
      />
      <div className={`text-sm mt-2 font-medium ${klaar ? 'text-emerald-800' : 'text-gray-600'}`}>
        {klaar
          ? 'Norm gehaald — flip van auto-revert naar ADS_AUTO_MUTATIES=live is verantwoord.'
          : revertsNok + verdictsNok > 0
            ? 'Geblokkeerd: er staat een afkeuring open. Eerst de oorzaak in auto-config.yaml of de skill repareren, dan herstart de klok.'
            : 'Nog niet genoeg bewijs — schaduw blijft staan tot het er is.'}
      </div>
    </div>
  );
}

/** Twee knoppen + reden-prompt; gedeeld door schaduw-, backtest- en verdict-rijen. */
function BeoordeelKnoppen({ opt }: { opt: OptimizationRow }) {
  const { beoordeel } = useAdsStore();
  const [bezig, setBezig] = useState(false);
  const [fout, setFout] = useState<string | null>(null);

  const klik = async (oordeel: 'akkoord' | 'afgekeurd') => {
    let reden: string | undefined;
    if (oordeel === 'afgekeurd') {
      const ingevoerd = window.prompt('Waarom niet? (verplicht — dit is de leerinput)');
      if (ingevoerd === null) return;
      if (!ingevoerd.trim()) {
        setFout('Een reden is verplicht bij een afkeuring');
        return;
      }
      reden = ingevoerd;
    } else {
      reden = window.prompt('Nuance of toelichting (optioneel):') ?? undefined;
    }
    setBezig(true);
    setFout(null);
    const err = await beoordeel(opt.id, oordeel, reden);
    if (err) setFout(err);
    setBezig(false);
  };

  if (opt.beoordeling) {
    const ok = opt.beoordeling === 'akkoord';
    return (
      <div className={`mt-2 text-sm rounded-lg px-3 py-2 ${ok ? 'bg-emerald-50 text-emerald-800' : 'bg-red-50 text-red-800'}`}>
        <span className="font-medium">{ok ? 'Akkoord' : 'Afgekeurd'}</span>
        {opt.beoordeeld_door && <span className="text-xs opacity-75"> — {opt.beoordeeld_door}</span>}
        {opt.beoordeling_reden && <div className="text-sm mt-0.5">{opt.beoordeling_reden}</div>}
      </div>
    );
  }

  return (
    <div className="mt-2">
      {fout && <div className="text-sm text-red-700 mb-1">{fout}</div>}
      <div className="flex items-center gap-2">
        <span className="text-xs text-gray-500 mr-1">Had je dit gewild?</span>
        <button
          onClick={() => klik('akkoord')}
          disabled={bezig}
          className="px-3 py-1 rounded-lg text-xs font-medium bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50"
        >
          Ja
        </button>
        <button
          onClick={() => klik('afgekeurd')}
          disabled={bezig}
          className="px-3 py-1 rounded-lg text-xs font-medium bg-red-600 text-white hover:bg-red-700 disabled:opacity-50"
        >
          Nee
        </button>
      </div>
    </div>
  );
}

/** Trede-3-voorstellen die op Willem wachten (vetovenster nog open). */
function ProposalsBlok() {
  const { optimizations, besluitProposal } = useAdsStore();
  const [bezig, setBezig] = useState<number | null>(null);
  const [fout, setFout] = useState<string | null>(null);

  const open = optimizations.filter(
    (o) => o.source === 'proposal' && o.verdict_status === 'proposed' && vetoUrenOver(o) !== null
  );
  if (!open.length) return null;

  const beslis = async (id: number, besluit: 'veto' | 'akkoord') => {
    const reden =
      besluit === 'veto'
        ? window.prompt('Reden voor het veto (optioneel):') ?? undefined
        : undefined;
    setBezig(id);
    setFout(null);
    const err = await besluitProposal(id, besluit, reden);
    if (err) setFout(`#${id}: ${err}`);
    setBezig(null);
  };

  return (
    <div className="mb-4 rounded-xl border border-purple-200 bg-purple-50 p-4">
      <div className="text-sm font-semibold text-purple-900 mb-2">
        Wacht op jou — {open.length} voorstel{open.length > 1 ? 'len' : ''} met open vetovenster
      </div>
      {fout && <div className="text-sm text-red-700 mb-2">{fout}</div>}
      <div className="space-y-3">
        {open.map((opt) => {
          const uren = vetoUrenOver(opt);
          return (
            <div key={opt.id} className="bg-white rounded-lg border border-purple-100 p-3">
              <div className="flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <div className="text-sm font-medium text-gray-900">{opt.change_summary}</div>
                  {opt.reason && <div className="text-sm text-gray-600 mt-0.5">Waarom: {opt.reason}</div>}
                  {opt.campaign_name && (
                    <div className="text-xs text-gray-500 mt-0.5">{opt.campaign_name}</div>
                  )}
                </div>
                <div className="shrink-0 text-right">
                  <div className="text-xs text-purple-700 font-medium mb-2">
                    nog {uren !== null && uren >= 1 ? `${Math.floor(uren)} uur` : '< 1 uur'}
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => beslis(opt.id, 'akkoord')}
                      disabled={bezig === opt.id}
                      className="px-3 py-1 rounded-lg text-xs font-medium bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50"
                    >
                      Akkoord
                    </button>
                    <button
                      onClick={() => beslis(opt.id, 'veto')}
                      disabled={bezig === opt.id}
                      className="px-3 py-1 rounded-lg text-xs font-medium bg-red-600 text-white hover:bg-red-700 disabled:opacity-50"
                    >
                      Veto
                    </button>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
      <div className="text-xs text-purple-700 mt-2">
        Zonder besluit wordt het voorstel na het venster automatisch uitgevoerd (binnen de
        guardrails). Uitvoering loopt altijd via apply.py, nooit via deze pagina.
      </div>
    </div>
  );
}

function BeforeAfter({ opt }: { opt: OptimizationRow }) {
  const b = opt.computed_before;
  const a = opt.computed_after;
  if (!b?.days || !a?.days) return null;

  const cpaDelta = b.cpa && a.cpa ? ((a.cpa - b.cpa) / b.cpa) * 100 : null;

  return (
    <div className="mt-3 grid grid-cols-3 gap-3 text-sm bg-gray-50 rounded-lg p-3">
      <div>
        <div className="text-xs text-gray-400">CPA vóór ({b.days}d)</div>
        <div className="font-medium text-gray-900">{eur(b.cpa)}</div>
      </div>
      <div>
        <div className="text-xs text-gray-400">CPA ná ({a.days}d)</div>
        <div className="font-medium text-gray-900">{eur(a.cpa)}</div>
      </div>
      <div>
        <div className="text-xs text-gray-400">Verschil</div>
        <div className={`font-medium ${deltaColor(cpaDelta, true)}`}>{delta(cpaDelta)}</div>
      </div>
    </div>
  );
}

function OptimizationsPanel() {
  const { optimizations } = useAdsStore();

  if (!optimizations.length) {
    return (
      <p className="text-gray-500 text-sm">
        Nog geen eigen optimalisaties gelogd. Wijzigingen via Claude (/ads apply) verschijnen hier
        automatisch, inclusief voor/na-resultaat.
      </p>
    );
  }

  // Open voorstellen staan bovenaan in het Wacht-op-jou-blok; hier de rest.
  const rijen = optimizations.filter(
    (o) => !(o.source === 'proposal' && o.verdict_status === 'proposed' && vetoUrenOver(o) !== null)
  );

  return (
    <div>
      <GateKlok />
      <ProposalsBlok />
      <div className="space-y-3">
      {rijen.map((opt: OptimizationRow) => {
        const actor = ACTOR_LABELS[opt.actor] ?? { label: opt.actor, cls: 'bg-gray-100 text-gray-600' };
        const verdict = VERDICT_LABELS[opt.verdict_status] ?? { label: 'Loopt', cls: 'bg-amber-100 text-amber-800' };
        const isSchaduw = opt.source === 'shadow';
        const isBacktest = opt.source === 'backtest';
        // Beoordeelbaar: een voorgenomen mutatie (schaduw/backtest) of een verdict
        // dat de autonome run zelf schreef. Historische verdicts uit een sessie met
        // Willem krijgen geen knoppen — die toetsen de autonomie niet.
        const teBeoordelen =
          isSchaduw || isBacktest || Boolean(opt.verdict && opt.actor === 'claude-cron');
        const gateGeraakt = Boolean(opt.gate_hit_at) && ['open', 'proposed'].includes(opt.verdict_status);
        return (
          <div
            key={opt.id}
            className={`rounded-xl border p-4 ${
              gateGeraakt
                ? 'bg-red-50 border-red-300'
                : isSchaduw || isBacktest
                  ? 'bg-gray-50 border-dashed border-gray-300'
                  : 'bg-white border-gray-200'
            }`}
          >
            <div className="flex items-center justify-between gap-3 mb-2">
              <div className="flex items-center gap-2 min-w-0">
                <span className={`px-2 py-0.5 rounded-full text-xs font-medium shrink-0 ${actor.cls}`}>{actor.label}</span>
                {isSchaduw && (
                  <span className="px-2 py-0.5 rounded-full text-xs font-medium shrink-0 bg-gray-200 text-gray-700">
                    Schaduw
                  </span>
                )}
                {isBacktest && (
                  <span
                    className="px-2 py-0.5 rounded-full text-xs font-medium shrink-0 bg-sky-100 text-sky-800"
                    title="Wat de logica op historische data gedaan zou hebben — nooit uitgevoerd."
                  >
                    Backtest
                  </span>
                )}
                {gateGeraakt && (
                  <span className="px-2 py-0.5 rounded-full text-xs font-medium shrink-0 bg-red-600 text-white">
                    Gate geraakt
                  </span>
                )}
                <span className="text-xs text-gray-500 uppercase tracking-wide shrink-0">{opt.category}</span>
                {opt.campaign_name && (
                  <span className="text-xs text-gray-500 truncate">{opt.campaign_name}</span>
                )}
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${verdict.cls}`}>{verdict.label}</span>
                <span className="text-xs text-gray-400">
                  {new Date(opt.changed_at).toLocaleDateString('nl-NL', { day: 'numeric', month: 'short' })}
                </span>
              </div>
            </div>

            <div className="text-sm font-medium text-gray-900">{opt.change_summary}</div>
            {opt.reason && <div className="text-sm text-gray-600 mt-1">Waarom: {opt.reason}</div>}
            {opt.expected_effect && (
              <div className="text-sm text-gray-600 mt-0.5">Verwacht: {opt.expected_effect}</div>
            )}

            <BeforeAfter opt={opt} />

            {opt.verdict ? (
              <div className="mt-2 text-sm text-gray-700">
                <span className="font-medium">Uitkomst:</span> {opt.verdict}
              </div>
            ) : opt.evaluate_on ? (
              <div className="mt-2 text-xs text-gray-400">
                Evaluatie gepland: {new Date(opt.evaluate_on).toLocaleDateString('nl-NL', { day: 'numeric', month: 'long' })}
              </div>
            ) : null}

            {teBeoordelen && <BeoordeelKnoppen opt={opt} />}
          </div>
        );
      })}
      </div>
    </div>
  );
}

// ─── Main Page ──────────────────────────────────────────────────────────────

export function AdsPage() {
  const { isLoading, error, dateRange, setDateRange, activeTab, setActiveTab, fetchAll } = useAdsStore();

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const tabs = [
    { key: 'overview' as const, label: 'Campagnes' },
    { key: 'search-terms' as const, label: 'Zoektermen' },
    { key: 'geo' as const, label: 'Regio' },
    { key: 'changes' as const, label: 'Wijzigingen' },
    { key: 'optimizations' as const, label: 'Optimalisaties' },
  ];

  return (
    <>
      <div className="flex-1 overflow-y-auto">
        <div className="max-w-6xl mx-auto px-6 py-8">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Google Ads</h1>
              <p className="text-sm text-gray-500 mt-1">Campagne prestaties en zoektermen analyse</p>
            </div>
            <div className="flex gap-2">
              {[7, 14, 30, 90].map((d) => (
                <button
                  key={d}
                  onClick={() => setDateRange(d)}
                  className={`px-3 py-1.5 rounded-lg text-sm font-medium ${
                    dateRange === d ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  {d}d
                </button>
              ))}
            </div>
          </div>

          {/* Error */}
          {error && (
            <div className="bg-red-50 text-red-700 rounded-xl px-4 py-3 text-sm mb-6">
              {error}
            </div>
          )}

          {/* Loading */}
          {isLoading && (
            <div className="flex items-center justify-center py-20">
              <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" />
            </div>
          )}

          {!isLoading && (
            <>
              <SummaryBar />

              {/* Tabs */}
              <div className="flex gap-1 mb-6 bg-gray-100 rounded-lg p-1 w-fit">
                {tabs.map((tab) => (
                  <button
                    key={tab.key}
                    onClick={() => setActiveTab(tab.key)}
                    className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                      activeTab === tab.key ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-900'
                    }`}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>

              {/* Tab Content */}
              {activeTab === 'overview' && <CampaignTable />}
              {activeTab === 'search-terms' && <SearchTermsPanel />}
              {activeTab === 'geo' && <GeoTable />}
              {activeTab === 'changes' && <ChangeLog />}
              {activeTab === 'optimizations' && <OptimizationsPanel />}
            </>
          )}
        </div>
      </div>
    </>
  );
}
